import React, { useMemo } from 'react';
import { Box, Grid, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import dayjs from 'dayjs';
import { formatValue } from '../../utils/format';
import { daysUntilQuotationDue } from '../tables/OpportunitiesTable';

function StatCard({ icon, label, value, color }) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        height: '100%',
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(theme.palette[color].main, 0.12),
          color: `${color}.main`,
        }}
      >
        {icon}
      </Box>
      <Stack spacing={0} sx={{ minWidth: 0 }}>
        <Typography variant="h6" fontWeight={800} noWrap>{value}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
      </Stack>
    </Paper>
  );
}

// สรุปตัวเลขคำนวณจาก "rows ที่ scroll โหลดมาแล้ว" เท่านั้น (useOpportunities.js โหลดทีละ 20
// แถวแบบ infinite scroll) — ไม่ได้ยิง query สรุปแยกจาก backend ตั้งใจให้เบาและไม่เพิ่ม
// endpoint ใหม่ ดังนั้น label ใช้คำว่า "รายการที่แสดง" ไม่ใช่ "ทั้งหมด" กันเข้าใจผิดว่าเป็น
// สรุปทั้งระบบ
export function OpportunityStatsBar({ rows }) {
  const stats = useMemo(() => {
    const loaded = rows.length;
    const totalValue = rows.reduce((sum, r) => sum + (Number(r.value) || 0), 0);
    const wonThisMonth = rows.filter((r) => r.status === 'WON' && r.date_end && dayjs(r.date_end).isSame(dayjs(), 'month')).length;
    const nearDue = rows.filter((r) => {
      const days = daysUntilQuotationDue(r);
      return days !== null && days >= 0 && days <= 3;
    }).length;
    return { loaded, totalValue, wonThisMonth, nearDue };
  }, [rows]);

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={6} md={3}>
        <StatCard
          icon={<BusinessCenterOutlinedIcon />}
          label="รายการที่แสดง"
          value={stats.loaded.toLocaleString('th-TH')}
          color="primary"
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <StatCard
          icon={<AttachMoneyOutlinedIcon />}
          label="มูลค่ารวม (Quoted Price)"
          value={formatValue(stats.totalValue)}
          color="info"
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <StatCard
          icon={<CheckCircleOutlinedIcon />}
          label="ได้งานแล้ว (เดือนนี้)"
          value={stats.wonThisMonth.toLocaleString('th-TH')}
          color="success"
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <StatCard
          icon={<EventBusyOutlinedIcon />}
          label="ใกล้ครบกำหนดยื่นราคา"
          value={stats.nearDue.toLocaleString('th-TH')}
          color="warning"
        />
      </Grid>
    </Grid>
  );
}

export default OpportunityStatsBar;
